"use client";

import { useMainLayout } from "@/app/context/mainLayoutProvider";
import React from "react";
import LinkItem from "./LinkItem";
import { navItems } from "@/lib/utils";
import { useGlobalStore } from "@/app/context/globalStoreProvider";
import { UserStatus } from "@/generated/email_parser.openapi";
import { usePathname, useSearchParams } from "next/navigation";
import { useUI } from "@/app/context/UIProvider";
import { usePostHog } from "posthog-js/react";

type Props = {
  isLoggedIn?: boolean;
};

const MiddleComponent: React.FC<Props> = ({ isLoggedIn }) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { renderMiddleContent } = useMainLayout();
  const { user } = useGlobalStore();
  const { isLessThanMd } = useUI();
  const posthog = usePostHog();
  const _isLoggedIn = isLoggedIn || user?.status === UserStatus.SiteAccessFull;

  // On (public) pages, GlobalStoreProvider is absent so user is undefined.
  // Fall back to the session-based isLoggedIn prop to show nav items.
  const hasFullAccess =
    user?.status === UserStatus.EmailSynced ||
    user?.status === UserStatus.SiteAccessFull ||
    (!user && isLoggedIn);

  if (
    pathname?.startsWith("/onboarding") &&
    searchParams.get("how-it-works") !== "true"
  )
    return null;

  // If we have custom middle content, return it with full width
  if (renderMiddleContent) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        {renderMiddleContent}
      </div>
    );
  }

  if (!_isLoggedIn) {
    return null;
  }

  return (
    <div className="flex h-full w-full items-center justify-center gap-6">
      {/* Nav tabs (Search, Points, Trips, Explore) only at high breakpoint; at low breakpoint they live in the hamburger menu */}
      {!isLessThanMd && hasFullAccess && (
        <div className="inline-flex items-center justify-start gap-2">
          {navItems
            .filter(
              (item) =>
                !("featureFlag" in item) ||
                posthog?.isFeatureEnabled(
                  (item as { featureFlag: string }).featureFlag
                )
            )
            .map((item) => (
              <LinkItem path={item.href} key={item.key}>
                <span>{item.title}</span>
              </LinkItem>
            ))}
        </div>
      )}
    </div>
  );
};

export default MiddleComponent;
